import type { Wallet } from '@4players/fleet';

export type FleetResourceKind = 'cpu' | 'memory';

/**
 * One independently enforced Fleet resource, folded across every wallet of
 * the same family. CPU and memory deliberately never share an accumulator.
 */
export interface FleetResourceWallet {
  kind: FleetResourceKind;
  label: string;
  unit: 'CPUh' | 'GBh';
  balance: number;
  initialBalance: number;
  used: number;
  usedPercent: number | null;
  rechargeAt: string | null;
}

const RESOURCE_DEFINITIONS: ReadonlyArray<{
  kind: FleetResourceKind;
  codePrefix: 'CPUH' | 'RAMH';
  label: string;
  unit: 'CPUh' | 'GBh';
}> = [
  {
    kind: 'cpu',
    codePrefix: 'CPUH',
    label: 'CPU Hours',
    unit: 'CPUh',
  },
  {
    kind: 'memory',
    codePrefix: 'RAMH',
    label: 'Memory GB Hours',
    unit: 'GBh',
  },
];

/**
 * Collapse Fleet's plan-specific wallet codes into two independent resources.
 *
 * `CPUH`, `CPUH_INDIE`, `CPUH_MINI`, ... form the CPU resource; the equivalent
 * `RAMH*` codes form memory. Several wallets in one family are additive, while
 * currency wallets and the other resource family are ignored.
 */
export function aggregateFleetResourceWallets(
  wallets: readonly Wallet[],
): FleetResourceWallet[] {
  const resources: FleetResourceWallet[] = [];

  for (const definition of RESOURCE_DEFINITIONS) {
    const matching = wallets.filter((wallet) =>
      wallet.currency?.code?.startsWith(definition.codePrefix),
    );
    if (matching.length === 0) {
      continue;
    }

    const balance = matching.reduce((sum, wallet) => sum + wallet.balance, 0);
    const initialBalance = matching.reduce(
      (sum, wallet) => sum + wallet.initialBalance,
      0,
    );
    const used = Math.max(0, initialBalance - balance);
    const usedPercent =
      initialBalance > 0
        ? Math.max(0, Math.min(1, used / initialBalance))
        : null;
    const rechargeAt =
      matching
        .map((wallet) => wallet.rechargeAt)
        .filter((value): value is string => !!value)
        .sort()[0] ?? null;

    resources.push({
      kind: definition.kind,
      label: definition.label,
      unit: definition.unit,
      balance,
      initialBalance,
      used,
      usedPercent,
      rechargeAt,
    });
  }

  return resources;
}
